/**
 * @fileoverview Single source of truth for occurrence-removal empty-handling
 *   and the resume read-precedence reconciliation.
 * @module core-domain/src/inspection/occurrenceMutations
 *
 * Responsibilities:
 * - Own the canonical "what happens when removing an occurrence empties a
 *   violation?" rule (the question-sourced vs additional asymmetry) so mobile
 *   and tenant cannot drift apart on removal semantics.
 * - Own the resume read-precedence rule that decides whether the in-session
 *   additional-violations draft or the canonical rows are authoritative, so a
 *   removed carried violation never reappears on resume.
 *
 * Dependencies:
 * - ./violationResolution (CorrectionStatus type only) — keeps the correction
 *   stamp's status union in lock-step with the resolution predicate that reads
 *   it. Type-only import; no runtime coupling.
 *
 * Architecture:
 * - Pure, side-effect-free. No I/O, no Dexie, no API, no React. Safe to call
 *   from a service, a hook, a liveQuery read, or a component on either app.
 *
 * Notes:
 * - This collapses two previously-drifted mobile/tenant behaviours:
 *     1. Removal empty-handling: mobile uniformly dropped the whole violation;
 *        tenant kept a question-sourced violation with empty `occurrences[]`
 *        and only deleted the synthetic response for additional violations.
 *     2. Resume precedence: the mobile adapter let canonical rows shadow the
 *        `moduleViolationsDraft` whenever any canonical row existed, so a
 *        removed *carried* violation reappeared on the next load.
 * - It also owns the correction stamp (P4): the single way an occurrence is
 *   marked repaired/retracted, so the stamp shape cannot drift between the
 *   mobile and tenant repair UIs that previously hand-rolled it.
 *
 * Canonical empty-handling asymmetry (deliberate):
 *
 *   violation source | last occurrence removed ⇒
 *   -----------------|---------------------------------------------------------
 *   'question'       | KEEP the violation as an occurrence-less shell. The
 *                    | failing answer is still the authoritative signal; the
 *                    | inspector may add occurrences later.
 *   'additional'     | DROP the violation entirely (caller deletes the
 *                    | synthetic response). An ad-hoc violation with zero
 *                    | occurrences is meaningless ≡ inspector retracted it.
 */

import type { CorrectionStatus } from './violationResolution';

/**
 * @contract
 * Origin of a violation. Mirrors `Violation['source']` but kept local so the
 * removal rule does not depend on the full response-type surface. `string` is
 * accepted so callers can pass an untyped/legacy source without a cast; any
 * value other than 'additional' is treated as question-sourced (keep-on-empty).
 */
export type ViolationSource = 'question' | 'additional';

/**
 * @process Decide whether emptying a violation's `occurrences[]` should drop
 *   the whole violation (and its synthetic response) or keep it as a shell.
 *
 * Steps:
 * 1. Additional (ad-hoc) violations are dropped when emptied.
 * 2. Everything else (question-sourced, or any non-'additional' value) is kept.
 *
 * Inputs: the violation's `source` (optional / possibly untyped).
 * Outputs: `true` when an emptied violation should be dropped entirely.
 * Failure: none — pure total function; `undefined` ⇒ keep (false).
 * Idempotency: pure.
 */
export function shouldDropEmptiedViolation(
  source: ViolationSource | string | undefined,
): boolean {
  return source === 'additional';
}

/**
 * @contract
 * Outcome of removing one occurrence from a violation.
 *
 * Fields:
 * - `remainingOccurrences`: the occurrences left after removing the target,
 *   in original order (same element references — no copies of the items).
 * - `dropViolation`: when `true`, the caller must delete the whole violation
 *   (mobile) / its synthetic response (tenant) rather than write back an
 *   occurrence-less violation.
 * Rules: `dropViolation` is only ever `true` when `remainingOccurrences` is
 *   empty AND the source is additional.
 * Sync: pure value object; no persistence implications of its own.
 */
export interface OccurrenceRemovalResult<O> {
  remainingOccurrences: O[];
  dropViolation: boolean;
}

/**
 * @contract
 * Context that turns a removal into a soft-retract (P7). Supplied only when the
 * caller knows which inspection event is currently being conducted and can
 * stamp a real actor — i.e. the in-session conduct/repair UIs. When omitted,
 * removal falls back to the legacy hard-delete (used by pure projection paths
 * that have no actor/event context).
 *
 * Fields:
 * - `currentEventIndex`: the event being conducted now. An occurrence is
 *   "carried" iff its `discoveredInEventIndex` is strictly less than this.
 * - `fixedBy` / `fixedAt`: actor id + ISO timestamp recorded on the tombstone.
 * - `fixedNotes`: optional free-text justification.
 * Rules: a carried occurrence is retracted (kept as a resolved tombstone); a
 *   same-event occurrence is hard-removed (no prior snapshot to resurrect from).
 * Sync: pure value object; persistence is the caller's concern.
 */
export interface OccurrenceRetractContext {
  currentEventIndex: number;
  fixedBy: string;
  fixedAt: string;
  fixedNotes?: string;
}

/**
 * @process Decide whether an occurrence was carried forward from a PRIOR
 *   inspection event (and therefore exists in an earlier event snapshot that
 *   the completion rollup would otherwise resurrect).
 *
 * Steps:
 * 1. Read `discoveredInEventIndex`; if absent, treat as NOT carried (a fresh
 *    occurrence with no provenance cannot have a prior snapshot).
 * 2. Carried iff `discoveredInEventIndex < currentEventIndex`.
 *
 * Inputs: an occurrence-like object and the event index being conducted now.
 * Outputs: `true` when the occurrence predates the current event.
 * Failure: none — pure total function.
 * Idempotency: pure.
 */
export function isCarriedOccurrence(
  occurrence: { discoveredInEventIndex?: number },
  currentEventIndex: number,
): boolean {
  return (
    typeof occurrence.discoveredInEventIndex === 'number' &&
    occurrence.discoveredInEventIndex < currentEventIndex
  );
}

/**
 * @process Remove one occurrence (by `_id`) from a violation and decide the
 *   canonical empty-handling outcome. With a {@link OccurrenceRetractContext}
 *   a carried occurrence is SOFT-RETRACTED (kept, stamped `retracted`) instead
 *   of hard-deleted, so it survives the completion rollup as a resolved
 *   tombstone rather than being resurrected from a prior event snapshot (P7).
 *
 * Steps:
 * 1. If `retract` is supplied AND the target occurrence is carried (see
 *    {@link isCarriedOccurrence}), stamp it `retracted` via
 *    {@link applyOccurrenceCorrection} and keep it in place. The violation is
 *    never dropped in this branch (it still has a resolved occurrence).
 * 2. Otherwise filter out the occurrence whose `_id` matches `occurrenceId`.
 * 3. If nothing remains, consult {@link shouldDropEmptiedViolation} on the
 *    violation's `source` to decide whether the violation should be dropped.
 *
 * Inputs: the violation's current occurrences, the target occurrence id, the
 *   violation `source`, and optional retract context.
 * Outputs: {@link OccurrenceRemovalResult} — remaining occurrences + drop flag.
 * Failure: none — pure total function; a non-matching id leaves the array
 *   unchanged and (since it is non-empty) `dropViolation` stays `false`.
 * Idempotency: pure; removing an already-absent id is a no-op result. Retracting
 *   an already-retracted occurrence re-stamps it to an equal value.
 */
export function removeOccurrenceFromViolation<
  O extends { _id?: string; discoveredInEventIndex?: number },
>(
  occurrences: ReadonlyArray<O>,
  occurrenceId: string,
  source: ViolationSource | string | undefined,
  retract?: OccurrenceRetractContext,
): OccurrenceRemovalResult<O> {
  if (retract) {
    const target = occurrences.find((occ) => occ._id === occurrenceId);
    if (target && isCarriedOccurrence(target, retract.currentEventIndex)) {
      const stamped = applyOccurrenceCorrection(target, {
        correctionType: 'retracted',
        fixedBy: retract.fixedBy,
        fixedAt: retract.fixedAt,
        fixedInEventIndex: retract.currentEventIndex,
        fixedNotes: retract.fixedNotes,
      }) as O;
      const remainingOccurrences = occurrences.map((occ) =>
        occ._id === occurrenceId ? stamped : occ,
      );
      return { remainingOccurrences, dropViolation: false };
    }
  }
  const remainingOccurrences = occurrences.filter((occ) => occ._id !== occurrenceId);
  const dropViolation =
    remainingOccurrences.length === 0 && shouldDropEmptiedViolation(source);
  return { remainingOccurrences, dropViolation };
}

/**
 * @process Resolve which set of additional violations is authoritative when
 *   reconstituting an inspection on resume.
 *
 * Steps:
 * 1. If an in-session draft is present (an array, EVEN IF empty), it wins.
 *    The draft is seeded from canonical at session start and then records
 *    every add AND removal, so it already reflects the inspector's intent —
 *    an empty draft means "all additional violations were removed".
 * 2. Otherwise, if canonical rows exist, use them.
 * 3. Otherwise return `undefined` to signal the caller should fall back to its
 *    legacy projection.
 *
 * This is the fix for the carried-violation-reappears bug: canonical rows must
 * NOT override a present draft, or a removed carried violation comes back.
 *
 * Inputs: `canonical` rows and the `draft` (`undefined` ≡ no draft field;
 *   `[]` ≡ inspector cleared everything).
 * Outputs: the authoritative rows, or `undefined` when neither source applies.
 * Failure: none — pure total function.
 * Idempotency: pure.
 */
export function resolveResumeViolations<V>(input: {
  canonical: V[];
  draft: V[] | undefined;
}): V[] | undefined {
  if (input.draft !== undefined) return input.draft;
  if (input.canonical.length > 0) return input.canonical;
  return undefined;
}

// ---------------------------------------------------------------------------
// Correction stamp (P4) — the single way an occurrence is marked repaired or
// retracted, so the mobile and tenant repair UIs (and the inspection renderer)
// produce a byte-identical stamp instead of each hand-rolling the fields.
// ---------------------------------------------------------------------------

/**
 * @contract
 * Terminal correction outcomes that may be stamped onto an occurrence by an
 * inspector action (as opposed to {@link CorrectionStatus} values like
 * 'pending'/'inProgress' that describe an in-flight correction).
 *
 * - 'repaired'  — work was done; the violation was actually corrected.
 * - 'retracted' — it was never actually a violation; the inspector withdrew it.
 *
 * Both classify as RESOLVED by the resolution predicate. This is a strict
 * subset of {@link CorrectionStatus}.
 */
export type OccurrenceCorrectionType = Extract<
  CorrectionStatus,
  'repaired' | 'retracted'
>;

/**
 * @contract
 * Inputs required to stamp a correction onto an occurrence. Mirrors the
 * existing discovery `ActorStamp` shape (actor id + ISO timestamp + event
 * index) but for the fixed/correction side.
 *
 * Fields:
 * - `correctionType`: 'repaired' | 'retracted' (see {@link OccurrenceCorrectionType}).
 * - `fixedBy`: actor id that performed/verified the correction.
 * - `fixedAt`: ISO timestamp of the correction.
 * - `fixedInEventIndex`: optional inspection event in which it was fixed.
 * - `fixedNotes`: optional free-text justification.
 * Rules: empty-string `fixedNotes` is treated as absent (omitted from output).
 * Sync: pure value object; persistence is the caller's concern.
 */
export interface OccurrenceCorrectionInput {
  correctionType: OccurrenceCorrectionType;
  fixedBy: string;
  fixedAt: string;
  fixedInEventIndex?: number;
  fixedNotes?: string;
}

/**
 * @contract
 * The structured field-stamp written onto an occurrence when it is corrected.
 * `status` is forced to 'resolved' so the legacy lifecycle flag and the
 * authoritative `correctionStatus` never disagree.
 *
 * Fields: status, correctionStatus, fixedBy, fixedAt, fixedInEventIndex?,
 *   fixedNotes?.
 * Rules: `fixedInEventIndex`/`fixedNotes` are present only when supplied.
 * Sync: domain data on the `cases` document; not an audit-trail entry.
 */
export interface OccurrenceCorrectionStamp {
  status: 'resolved';
  correctionStatus: OccurrenceCorrectionType;
  fixedBy: string;
  fixedAt: string;
  fixedInEventIndex?: number;
  fixedNotes?: string;
}

/**
 * @process Build the canonical correction stamp from the supplied inputs.
 *
 * Steps:
 * 1. Force `status` to 'resolved' and copy the `correctionType` into
 *    `correctionStatus`.
 * 2. Copy `fixedBy`/`fixedAt` verbatim.
 * 3. Include `fixedInEventIndex` only when provided.
 * 4. Include `fixedNotes` only when it is a non-empty string.
 *
 * Inputs: {@link OccurrenceCorrectionInput}.
 * Outputs: {@link OccurrenceCorrectionStamp}.
 * Failure: none — pure total function.
 * Idempotency: pure; identical input always yields an identical stamp.
 */
export function buildOccurrenceCorrectionStamp(
  input: OccurrenceCorrectionInput,
): OccurrenceCorrectionStamp {
  const stamp: OccurrenceCorrectionStamp = {
    status: 'resolved',
    correctionStatus: input.correctionType,
    fixedBy: input.fixedBy,
    fixedAt: input.fixedAt,
  };
  if (input.fixedInEventIndex !== undefined) {
    stamp.fixedInEventIndex = input.fixedInEventIndex;
  }
  if (input.fixedNotes !== undefined && input.fixedNotes !== '') {
    stamp.fixedNotes = input.fixedNotes;
  }
  return stamp;
}

/**
 * @process Apply a correction stamp to an occurrence, returning a new object
 *   (the input is never mutated).
 *
 * Steps:
 * 1. Build the stamp via {@link buildOccurrenceCorrectionStamp}.
 * 2. Spread it over a shallow copy of the occurrence, overwriting the status
 *    and correction fields.
 *
 * Inputs: any occurrence-like object plus {@link OccurrenceCorrectionInput}.
 * Outputs: a new occurrence carrying the stamp.
 * Failure: none — pure total function.
 * Idempotency: pure; re-applying the same stamp yields an equal object.
 */
export function applyOccurrenceCorrection<O extends object>(
  occurrence: O,
  input: OccurrenceCorrectionInput,
): O & OccurrenceCorrectionStamp {
  return { ...occurrence, ...buildOccurrenceCorrectionStamp(input) };
}

/**
 * @contract
 * The cleared-correction shape — the inverse of {@link OccurrenceCorrectionStamp}.
 * Resets the occurrence to an actionable, unstamped state. Stamp fields are set
 * to `undefined` (rather than deleted) so the change is explicit on the patched
 * object for sync write-through.
 */
export interface ClearedOccurrenceCorrection {
  status: 'open';
  correctionStatus: undefined;
  fixedBy: undefined;
  fixedAt: undefined;
  fixedInEventIndex: undefined;
  fixedNotes: undefined;
}

/**
 * @process Undo a correction: clear the stamp and reopen the occurrence,
 *   returning a new object (the input is never mutated).
 *
 * Steps:
 * 1. Set `status` back to 'open'.
 * 2. Clear `correctionStatus` and every `fixed*` field to `undefined`.
 *
 * Inputs: any occurrence-like object.
 * Outputs: a new occurrence with the correction stamp cleared.
 * Failure: none — pure total function.
 * Idempotency: pure; clearing an already-cleared occurrence is a no-op result.
 */
export function clearOccurrenceCorrection<O extends object>(
  occurrence: O,
): O & ClearedOccurrenceCorrection {
  return {
    ...occurrence,
    status: 'open',
    correctionStatus: undefined,
    fixedBy: undefined,
    fixedAt: undefined,
    fixedInEventIndex: undefined,
    fixedNotes: undefined,
  };
}
